import React from 'react';
import { ShieldAlert, AlertCircle, TrendingDown, Users } from 'lucide-react';
import type { EntityOnlyAggregate } from '../../../types/acquisitionAnalysis';

interface ThirdPartyPaymentsProps {
  data: EntityOnlyAggregate;
}

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

const formatPercent = (value: number): string => `${value.toFixed(1)}%`;

/**
 * 3rd-Party Payment Analysis (Connective entity-only mode).
 * Renders gross→net leakage, ranked recipients, and per-month run pattern.
 */
export const ThirdPartyPayments: React.FC<ThirdPartyPaymentsProps> = ({ data }) => {
  const t12 = data.trailing12Months;

  const headerCards: Array<{
    label: string;
    value: string;
    sublabel?: string;
    color: string;
    icon: React.ComponentType<{ className?: string }>;
  }> = [
    {
      label: 'Total leakage (12 mo)',
      value: formatCurrency(t12.thirdPartyUpfront + t12.thirdPartyTrail),
      sublabel: 'Upfront + Trail combined',
      color: 'amber',
      icon: ShieldAlert,
    },
    {
      label: 'Upfront leakage % of gross',
      value: formatPercent(t12.upfrontLeakagePercent),
      sublabel: `${formatCurrency(t12.thirdPartyUpfront)} / ${formatCurrency(t12.grossUpfront)}`,
      color: 'amber',
      icon: TrendingDown,
    },
    {
      label: 'Trail leakage % of gross',
      value: formatPercent(t12.trailLeakagePercent),
      sublabel: `${formatCurrency(t12.thirdPartyTrail)} / ${formatCurrency(t12.grossTrail)}`,
      color: 'amber',
      icon: TrendingDown,
    },
    {
      label: 'Distinct recipients',
      value: String(data.topRecipients.length),
      sublabel: `${data.fileCount} RCTI files`,
      color: 'blue',
      icon: Users,
    },
  ];

  const colorClasses: Record<string, string> = {
    amber: 'bg-amber-500/15 border-amber-500/40 text-amber-300',
    blue: 'bg-blue-500/15 border-blue-500/40 text-blue-300',
  };

  return (
    <div className="space-y-6">
      {/* Banner */}
      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-sm font-bold text-amber-300 mb-1">
            Connective entity-level analysis
          </h4>
          <p className="text-xs text-amber-200/80">
            Per-broker breakdown is not available in Connective RCTIs. Top
            Brokers, Concentration, Retention, and Growth Trajectory tabs are
            hidden. Request a per-broker monthly schedule from the target
            before signing.
          </p>
        </div>
      </div>

      {/* Headline metrics */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Trailing 12 Months</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {headerCards.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.label}
                className={`p-4 rounded-lg border ${colorClasses[c.color]}`}
              >
                <Icon className="w-5 h-5 mb-2" />
                <p className="text-2xl font-bold text-white mb-1">{c.value}</p>
                <p className="text-xs text-slate-400">{c.label}</p>
                {c.sublabel && (
                  <p className="text-[11px] text-slate-500 mt-1">{c.sublabel}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Gross vs Net comparison */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Gross vs Net (12 mo)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700/50">
            <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-2 font-semibold">
              Upfront
            </h4>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-300">Gross</span>
                <span className="font-mono text-white">
                  {formatCurrency(t12.grossUpfront)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-amber-300">Less 3rd parties</span>
                <span className="font-mono text-amber-300">
                  −{formatCurrency(t12.thirdPartyUpfront)}
                </span>
              </div>
              <div className="border-t border-slate-700 pt-1.5 flex justify-between">
                <span className="text-emerald-300 font-semibold">Net</span>
                <span className="font-mono text-emerald-300 font-bold">
                  {formatCurrency(t12.netUpfront)}
                </span>
              </div>
            </div>
          </div>
          <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700/50">
            <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-2 font-semibold">
              Trail
            </h4>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-300">Gross</span>
                <span className="font-mono text-white">
                  {formatCurrency(t12.grossTrail)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-amber-300">Less 3rd parties</span>
                <span className="font-mono text-amber-300">
                  −{formatCurrency(t12.thirdPartyTrail)}
                </span>
              </div>
              <div className="border-t border-slate-700 pt-1.5 flex justify-between">
                <span className="text-emerald-300 font-semibold">Net</span>
                <span className="font-mono text-emerald-300 font-bold">
                  {formatCurrency(t12.netTrail)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top recipients table */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Top Recipients</h3>
        <div className="overflow-hidden rounded-lg border border-slate-700/50">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/70 text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Rank</th>
                <th className="px-3 py-2 text-left font-semibold">Recipient</th>
                <th className="px-3 py-2 text-right font-semibold">Occ.</th>
                <th className="px-3 py-2 text-right font-semibold">Upfront</th>
                <th className="px-3 py-2 text-right font-semibold">Trail</th>
                <th className="px-3 py-2 text-right font-semibold">Total</th>
                <th className="px-3 py-2 text-right font-semibold">% leakage</th>
              </tr>
            </thead>
            <tbody>
              {data.topRecipients.map((r, idx) => (
                <tr key={r.payeeName} className="border-t border-slate-800 hover:bg-slate-900/40">
                  <td className="px-3 py-2 text-slate-400">{idx + 1}</td>
                  <td className="px-3 py-2 text-white font-medium">{r.payeeName}</td>
                  <td className="px-3 py-2 text-right text-slate-300 font-mono">
                    {r.occurrences}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-300 font-mono">
                    {formatCurrency(r.upfront)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-300 font-mono">
                    {formatCurrency(r.trail)}
                  </td>
                  <td className="px-3 py-2 text-right text-white font-mono font-semibold">
                    {formatCurrency(r.total)}
                  </td>
                  <td className="px-3 py-2 text-right text-amber-300 font-mono">
                    {formatPercent(r.percentOfLeakage)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* RCTI run pattern */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-3">RCTI Run Pattern</h3>
        <div className="flex flex-wrap gap-2">
          {data.rctiRunsPerMonth.map((m) => {
            const intensity =
              m.runs >= 3 ? 'bg-indigo-500/60 border-indigo-400/80' :
              m.runs === 2 ? 'bg-indigo-500/35 border-indigo-500/60' :
              'bg-indigo-500/15 border-indigo-500/40';
            return (
              <div
                key={m.month}
                className={`flex flex-col items-center justify-center w-16 h-16 rounded-md border ${intensity}`}
              >
                <span className="text-[10px] text-slate-300">{m.month}</span>
                <span className="text-lg font-bold text-white">{m.runs}</span>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Number of RCTI files received per month. Connective issues 1–3 RCTIs
          per month per supplier; runs are summed in the analysis.
        </p>
      </div>

      {/* Provenance */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-3">Data Provenance</h3>
        <div className="text-xs text-slate-400 space-y-1 mb-3">
          <div>
            Supplier:{' '}
            <span className="text-slate-200 font-medium">
              {data.supplierName}
            </span>{' '}
            (ABN {data.supplierAbn || 'unknown'})
          </div>
          <div>
            Period: <span className="text-slate-200">{data.earliestPeriodStart}</span>{' '}
            → <span className="text-slate-200">{data.latestPeriodEnd}</span> · {data.monthsCovered}{' '}
            months · {data.fileCount} RCTI files
          </div>
          <div>
            Cross-checks (Gross + Less = Net; Σ payee rows = Total):{' '}
            {data.allCrossChecksPassed ? (
              <span className="text-emerald-400 font-medium">all passed</span>
            ) : (
              <span className="text-red-400 font-medium">some FAILED — see file list</span>
            )}
          </div>
        </div>
        <div className="overflow-auto max-h-72 rounded-lg border border-slate-700/50">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 sticky top-0">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold">File</th>
                <th className="px-2 py-1.5 text-left font-semibold">Period</th>
                <th className="px-2 py-1.5 text-right font-semibold">Net Up</th>
                <th className="px-2 py-1.5 text-right font-semibold">Net Tr</th>
                <th className="px-2 py-1.5 text-right font-semibold">3p#</th>
                <th className="px-2 py-1.5 text-center font-semibold">✓</th>
              </tr>
            </thead>
            <tbody>
              {data.perFile.map((f) => (
                <tr key={f.filename} className="border-t border-slate-800">
                  <td className="px-2 py-1 text-slate-300 truncate max-w-[200px]" title={f.filename}>
                    {f.filename}
                  </td>
                  <td className="px-2 py-1 text-slate-400">
                    {f.periodStart}
                  </td>
                  <td
                    className={`px-2 py-1 text-right font-mono ${
                      f.netUpfront < 0 ? 'text-red-400' : 'text-slate-300'
                    }`}
                  >
                    {formatCurrency(f.netUpfront)}
                  </td>
                  <td
                    className={`px-2 py-1 text-right font-mono ${
                      f.netTrail < 0 ? 'text-red-400' : 'text-slate-300'
                    }`}
                  >
                    {formatCurrency(f.netTrail)}
                  </td>
                  <td className="px-2 py-1 text-right text-slate-400 font-mono">
                    {f.thirdPartyCount}
                  </td>
                  <td className="px-2 py-1 text-center">
                    {f.crossChecksPassed ? (
                      <span className="text-emerald-400">✓</span>
                    ) : (
                      <span className="text-red-400">✕</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
